import widgets from '../../../widgets';
import io_widgets from '../../../instruction_widgets/io_widgets';
import { selectOption, selectComponent } from '../../../config_ui/config_components/base_components';
import { getFirstAvailableInputUuid } from '../../../../machinelogic/utils';
import { TooltipKey } from '../../../machinelogic_tooltips';

/**
 * returns event type dependent widgets for the target transition state modal
 * @param {Object} params
 * @param {Object} params.library the library object
 * @param {Object} params.localModalTargetState a deep copy of the target transition state
 * @returns {JQuery<HTMLElement>}
 */
export const targetStateEventEditor = ({library, localModalTargetState}) => {
  const lDiv = $('<div>').addClass('ml_fsm_target_state_triggering_event');

  const lRenderUi = () => {
    lDiv.empty();
    lDiv.append(
      eventTypeWidget({
        localModalTargetState,
        onEventTypeChange: lRenderUi
      })
    );
    if(localModalTargetState.eventType === 'mqtt') {
      lDiv.append(
        topicWidget({
          localModalTargetState,
          onTopicChange: lRenderUi
        })
      );
    } else {
      lDiv.append(
        digitalInputSelectWidget({library, localModalTargetState, onInputChange: lRenderUi}),
        digitalInputWatchValuesWidget({library, localModalTargetState, onInputChange: lRenderUi})
      );
    }
  };

  lRenderUi();
  return lDiv;
};


/**
 * @typedef {[ { value: 1, text: "1" }, { value: 0, text: "0" } ]} DigitalIoOption
 */

/**
 * @typedef {[ { value: 1, text: "Pushed" }, { value: 0, text: "Released" } ]} PushButtonOptions
 */
/**
 * returns a list of options dependent on the type of io.
 * @param {Object} pInput an input object
 * @returns {DigitalInputOptions | PushButtonOptions}
 */
const getDigitalInputDependentOptions = (pInput) => {
  if(!pInput) {
    return [
      { value: 1, text: "1" },
      { value: 0, text: "0" },
    ];
  }

  const lDeviceType = pInput.getDeviceType();
  if (lDeviceType === 'pushButtonModule') {
    return [
      { value: 1, text: "Pushed" },
      { value: 0, text: "Released" },
    ];
  }

  return [
    { value: 1, text: "1" },
    { value: 0, text: "0" },
  ];
};


/**
 * returns an input and input value based on the event definition
 * @param {Object} params
 * @param {string} params.eventDefinition
 * @param {string} params.defaultValue
 * @param {string} params.defaultUuid
 * @returns {Object}
 */
const getDigitalInputUuidAndValueFromState = ({eventDefinition, defaultValue, defaultUuid}) => {
  const isDigitalInputString = (pString) => pString !== undefined && pString.split('|').length === 2;
  if(!isDigitalInputString(eventDefinition)) {
    return { uuid: defaultUuid, value: defaultValue };
  }

  const [uuid, value] = eventDefinition.split('|');
  return { uuid, value };
};


/**
 * returns a concatenated string of input uuid and input value. uuid|value
 * @param {Object} params
 * @param {string} params.inputUuid
 * @param {string} params.pinValue
 * @returns {string}
 */
function eventDefinition({inputUuid, pinValue}) {
  return `${inputUuid}|${pinValue}`;
}


/**
 * returns an html select for inputs
 * @param {Object} params
 * @param {Object} params.library the library object
 * @param {Object} params.localModalTargetState a deep copy of the target transition state
 * @param {() => void} params.onInputChange callback for when the digital input value changes
 * @returns {JQuery<HTMLElement>}
 */
const digitalInputSelectWidget = ({library, localModalTargetState, onInputChange}) => {
  const lFirstInputUuid = getFirstAvailableInputUuid(library);
  const lDigitalInputProps = getDigitalInputUuidAndValueFromState({
    eventDefinition: localModalTargetState.eventDefinition,
    defaultValue: "0",
    defaultUuid: lFirstInputUuid
  });

  localModalTargetState.eventDefinition = eventDefinition({inputUuid: lDigitalInputProps.uuid, pinValue: lDigitalInputProps.value});

  return io_widgets.inputParamWidget(
    library.getConfigManager(),
    'Input Name',
    "Device triggering the transition",
    false,
    lDigitalInputProps.uuid,
    (pNewUuid) => {
      const lPinValue = getDigitalInputUuidAndValueFromState(localModalTargetState).value;
      localModalTargetState.eventDefinition = `${pNewUuid}|${lPinValue}`;
      onInputChange();
    },
    false
  ).addClass('v_layout spaced');
};

/**
 * returns an html select for the input value to be watched
 * @param {Object} params
 * @param {Object} params.library the library object
 * @param {Object} params.localModalTargetState a deep copy of the target transition state
 * @param {() => void} params.onInputChange callback for when the digital input value changes
 * @returns {JQuery<HTMLElement>}
 */
const digitalInputWatchValuesWidget = ({library, localModalTargetState, onInputChange}) => {
  const lFirstInputUuid = getFirstAvailableInputUuid(library);
  const lDigitalInputProps = getDigitalInputUuidAndValueFromState({
    eventDefinition: localModalTargetState.eventDefinition,
    defaultValue: "0",
    defaultUuid: lFirstInputUuid
  });

  localModalTargetState.eventDefinition = eventDefinition({inputUuid: lDigitalInputProps.uuid, pinValue: lDigitalInputProps.value});

  const lConfiguredInput = library.getConfigManager().getInputByUuid(lDigitalInputProps.uuid);
  const lInputOptions = getDigitalInputDependentOptions(lConfiguredInput);

  return widgets.listWidget(
    "Level",
    lInputOptions,
    "Input level triggering the transition",
    lDigitalInputProps.value,
    (pPinValue) => {
      localModalTargetState.eventDefinition = `${lDigitalInputProps.uuid}|${pPinValue}`;
      onInputChange();
    },
    false,
    false
  );
};

/**
 * returns an html input field for a watched mqtt topic
 * @param {Object} params
 * @param {Object} params.localModalTargetState a deep copy of the target transition state
 * @param {() => void} params.onTopicChange callback for when the topic changes
 * @returns {JQuery<HTMLElement>}
 */
const topicWidget = ({localModalTargetState, onTopicChange}) => {
  return widgets.stringWidget(
    'Topic',
    'Topic triggering the transition',
    localModalTargetState.eventDefinition,
    (pNewEventDefinition) => {
      localModalTargetState.eventDefinition = pNewEventDefinition;
      onTopicChange();
    },
    null,
    undefined
  ).addClass('v_layout spaced');
};


/**
 * returns an html select of the different types of topics to be watched for an fsm state transition
 * @param {Object} params
 * @param {Object} params.localModalTargetState a deep copy of the target transition state
 * @param {() => void} params.onEventTypeChange callback for when the topic changes
 * @returns {JQuery<HTMLElement>}
 */
const eventTypeWidget = ({localModalTargetState, onEventTypeChange}) => {
  const FsmEventTypes = [
    {value: 'mqtt', text: 'Topic'},
    {value: 'digital_input', text: 'Digital Input'}
  ];
  const lEventTypeOptions = FsmEventTypes.map(pOption => selectOption(pOption.value, pOption.text, false, false));
  const lEventTypeWidget = selectComponent(
    'Event Type',
    lEventTypeOptions,
    localModalTargetState.eventType,
    'Type of event.',
    (pNewEventType) => {
      localModalTargetState.eventType = pNewEventType;
      if(pNewEventType === 'mqtt') {
        localModalTargetState.eventDefinition = ""; // reset the eventDefinition otherwise we see the value for the digital input
      }
      onEventTypeChange();
    },
    { selectDivClass: 'ml_select_widget', tooltipKey: TooltipKey.FSM_TRANSITION_EVENT_TYPE }
  ).widget;


  return lEventTypeWidget;
};
